// PATH: src/components/documents/DocumentChapterBlock.tsx
import * as React from "react";
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";

import type { Document, Track } from "../../models/domain";
import { useDocumentsApi } from "../../api/documents/documents";
import { buildChapterPatch, chapterFormFromDocument, type ChapterForm } from "./documentChapter";
import { errorMessage, useToast } from "../ui";

interface Props {
  document: Document;
  /** Filières du niveau du chapitre (catalogue, déjà chargé par la page). */
  tracks: Track[];
  /** Identité de chapitre enregistrée : le document à jour. */
  onSaved?: (doc: Document) => void;
}

/**
 * Identité du chapitre (référence, semestre, ordre, filière). Elle s'enregistre
 * SEULE, par `PATCH /v1/documents/:id/chapter` : le PUT du document n'écrit
 * jamais ces champs.
 */
export const DocumentChapterBlock: React.FC<Props> = ({ document, tracks, onSaved }) => {
  const { patchDocumentChapter } = useDocumentsApi();
  const toast = useToast();

  const [saved, setSaved] = React.useState<Document>(document);
  const [form, setForm] = React.useState<ChapterForm>(() => chapterFormFromDocument(document));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setSaved(document);
    setForm(chapterFormFromDocument(document));
    setError(null);
  }, [document]);

  const result = buildChapterPatch(saved, form);
  const dirty = !result.ok || Object.keys(result.patch).length > 0;
  // Filière enregistrée mais absente du catalogue (désactivée, supprimée) : montrée quand même.
  const unknownTrack = form.trackId && !tracks.some((t) => t.id === form.trackId);

  const handleSave = async () => {
    setError(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    if (Object.keys(result.patch).length === 0) return;
    setSaving(true);
    try {
      const { data } = await patchDocumentChapter(document.id, result.patch);
      const next = { ...saved, ...result.patch, ...(data ?? {}) } as Document;
      setSaved(next);
      setForm(chapterFormFromDocument(next));
      toast.success("Identité du chapitre enregistrée.");
      onSaved?.(next);
    } catch (e: any) {
      setError(
        e?.response?.status === 409
          ? "Cette référence de chapitre est déjà prise dans le programme."
          : errorMessage(e, "Échec de l'enregistrement du chapitre."),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="subtitle1">Identité du chapitre</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Place du chapitre dans le programme. Normalement posée par le manifeste à l'import.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        <TextField
          label="Référence du chapitre"
          value={form.chapterRef}
          onChange={(e) => setForm((p) => ({ ...p, chapterRef: e.target.value }))}
          helperText="Vide = document hors chapitre"
          fullWidth
          InputProps={{ className: "mono" }}
        />
        <Stack direction="row" spacing={2}>
          <TextField
            label="Semestre"
            type="number"
            value={form.semester}
            onChange={(e) => setForm((p) => ({ ...p, semester: e.target.value }))}
            inputProps={{ min: 1, inputMode: "numeric" }}
            fullWidth
          />
          <TextField
            label="Ordre"
            type="number"
            value={form.chapterOrder}
            onChange={(e) => setForm((p) => ({ ...p, chapterOrder: e.target.value }))}
            inputProps={{ min: 1, inputMode: "numeric" }}
            fullWidth
          />
        </Stack>
        <TextField
          select
          label="Filière"
          value={form.trackId}
          onChange={(e) => setForm((p) => ({ ...p, trackId: e.target.value }))}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          helperText={tracks.length === 0 && !form.trackId ? "Ce niveau n'a pas de filière." : undefined}
          fullWidth
        >
          <MenuItem value="">Commun (toutes les filières)</MenuItem>
          {tracks.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name} ({t.code}){t.is_active ? "" : " — inactive"}
            </MenuItem>
          ))}
          {unknownTrack ? <MenuItem value={form.trackId}>{form.trackId} (hors catalogue)</MenuItem> : null}
        </TextField>

        <Box display="flex" justifyContent="flex-end" gap={1}>
          {dirty && (
            <Button color="inherit" onClick={() => setForm(chapterFormFromDocument(saved))} disabled={saving}>
              Annuler
            </Button>
          )}
          <Button variant="outlined" onClick={() => void handleSave()} disabled={saving || !dirty}>
            {saving ? "Enregistrement…" : "Enregistrer l'identité"}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default DocumentChapterBlock;
